import styled from '@emotion/styled'
import {
    priceFormat,
    formatPriceWithoutSymbol,
    splitDollarsAndCents,
} from '../../../utils/priceFormat'
import { useContext } from 'react'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import {
    PRIMARY_FONT_CSS_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SUBTLE_PRIMARY_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { getTypographyRoleStyle } from '../../../style/typography'
import { scaledFontSize } from '../../../style/scrollingLayout'

const PriceRow = styled.div({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
})

const DiscountText = styled.span({
    color: '#C41E3A',
    fontSize: scaledFontSize(24),
    ...getTypographyRoleStyle('badgeText', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        fontWeight: 400,
        lineHeight: 1,
    }),
})

const PriceContainer = styled.span({
    display: 'inline-flex',
    alignItems: 'flex-start',
    fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
})

const CurrencySymbol = styled.span({
    fontSize: scaledFontSize(14),
    ...getTypographyRoleStyle('price', {
        fontWeight: 400,
        lineHeight: 1,
    }),
    marginTop: 2,
})

const PriceDollars = styled.span({
    fontSize: scaledFontSize(28),
    ...getTypographyRoleStyle('price', {
        fontWeight: 400,
        lineHeight: 1,
    }),
})

const PriceCents = styled.span({
    fontSize: scaledFontSize(14),
    ...getTypographyRoleStyle('price', {
        fontWeight: 400,
        lineHeight: 1,
    }),
    marginTop: 2,
})

const ListPriceRow = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
})

const ListPriceLabel = styled.span({
    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
    fontSize: scaledFontSize(13),
    ...getTypographyRoleStyle('captionText', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        fontWeight: 400,
    }),
})

const ListPriceValue = styled.span({
    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
    fontSize: scaledFontSize(13),
    ...getTypographyRoleStyle('compareAtPrice', {
        fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
        fontWeight: 400,
    }),
    textDecoration: 'line-through',
})

export const PriceMainRow: React.FC<{
    price: number
    discountPercentage?: number | null
}> = ({ price, discountPercentage }) => {
    const { landingPage } = useContext(ViddyLPContext)

    const currencySymbol = landingPage?.client?.currencySymbol || '$'
    const stringPrice = formatPriceWithoutSymbol(price)
    const { dollars, cents } = splitDollarsAndCents(stringPrice)

    const hasDiscount =
        typeof discountPercentage === 'number' && discountPercentage > 0

    return (
        <PriceRow>
            {hasDiscount && <DiscountText>-{discountPercentage}%</DiscountText>}
            <PriceContainer>
                <CurrencySymbol>{currencySymbol}</CurrencySymbol>
                <PriceDollars>{dollars}</PriceDollars>
                <PriceCents>{cents}</PriceCents>
            </PriceContainer>
        </PriceRow>
    )
}

export const CompareAtPriceRow: React.FC<{
    price: number
    compareAtPrice: number | null
}> = ({ price, compareAtPrice }) => {
    const { landingPage } = useContext(ViddyLPContext)

    const shouldShowComparePrice =
        compareAtPrice !== null && compareAtPrice > price

    if (!shouldShowComparePrice) return null

    return (
        <ListPriceRow>
            <ListPriceLabel>List Price:</ListPriceLabel>
            <ListPriceValue>
                {priceFormat(
                    compareAtPrice,
                    landingPage?.client?.currencySymbol,
                )}
            </ListPriceValue>
        </ListPriceRow>
    )
}
